import { useRef, useEffect, useState, useCallback } from "react";
import { gsap } from "gsap";
import { MessageCircle, ArrowRight, ExternalLink, Github } from "lucide-react";
import "./MagicBento.css";

const DEFAULT_PARTICLE_COUNT = 12;
const DEFAULT_SPOTLIGHT_RADIUS = 300;
const DEFAULT_GLOW_COLOR = "132, 0, 255";
const MOBILE_BREAKPOINT = 768;

const cardData = [
  {
    color: "#060010",
    title: "Web Auditing",
    description: "Performance optimization, security, and SEO auditing with AI powered insights",
    label: "Intelligence",
    media: {
      video: "https://storage.googleapis.com/tll-videos-for-web/WebsiteSkills_WebAuditing.mp4",
      poster: "/img/WebsiteSkills_StaticImages/WebAuditing.jpg",
      alt: "Neural network visualization"
    },
    links: {
      github: ["https://github.com/jaywalked78/WebAuditTool"]
    },
    chatPrompt: "Show me examples of web auditing projects Jason has built involving both front-end and back-end technologies with n8n",
    stats: {
      projects: 12,
      languages: ["Python", "TensorFlow", "PyTorch"],
      highlight: "Custom NLP model with 95% accuracy"
    }
  },
  {
    color: "#060010",
    title: "Data Architecture",
    description: "Database design and integration into front-end and back-end applications",
    label: "Complete",
    media: {
      video: "https://storage.googleapis.com/tll-videos-for-web/WebsiteSkills_DataArchitecture.mp4",
      poster: "/img/WebsiteSkills_StaticImages/DataArchitecture.jpg",
      alt: "Full stack application demo"
    },
    links: {
      github: ["https://github.com/jaywalked78/LightragFork_AdvancedNeo4JImplementation", "https://github.com/jaywalked78/Lightweight-File-Hosting-Server", "https://github.com/jaywalked78/IntelliChunk"]
    },
    chatPrompt: "What database integrations with PostgreSQL and/or Neo4j has Jason built?",
    stats: {
      projects: 18,
      tools: ["React", "Node.js", "Neo4j", "PostgreSQL", "Python", "n8n"],
      highlight: "Database creation, document ingestion, and fully accurate search and retrieval capabilities"
    }
  },
  {
    color: "#060010",
    title: "Front-End UI",
    description: "Interactive user interfaces & design systems",
    label: "Interface",
    media: {
      video: "https://storage.googleapis.com/tll-videos-for-web/WebsiteSkills_FrontendUI.mp4",
      poster: "/img/WebsiteSkills_StaticImages/FrontendUI.jpg",
      alt: "Front-end UI development"
    },
    links: {
      github: ["https://github.com/jaywalked78/TLL-WebBuild-Final", "https://github.com/jaywalked78/Resi-Dev-Challenge", "interactive-demos"],
      live: ["https://ui-demo.thelogicloom.com"]
    },
    chatPrompt: "Show me front-end UI and design system projects Jason has built",
    stats: {
      projects: 22,
      languages: ["React", "Next.js", "TypeScript", "Tailwind"],
      highlight: "This website was built with React and Tailwind"
    }
  },
  {
    color: "#060010",
    title: "Automation",
    description: "Process optimization & workflow automation",
    label: "Efficiency",
    media: {
      video: "https://storage.googleapis.com/tll-videos-for-web/WebsiteSkills_AutomationV2.mp4",
      poster: "/img/WebsiteSkills_StaticImages/AutomationV2.jpg",
      alt: "Automation workflow visualization"
    },
    links: {
      github: ["workflow-automation", "process-optimizer", "task-scheduler"],
      live: ["https://automation-demo.thelogicloom.com"]
    },
    chatPrompt: "What automation workflows has Jason built?",
    stats: {
      projects: 15,
      languages: ["Python", "JavaScript", "Shell Scripting"],
      highlight: "Built nearly 100+ automation solutions for various projects"
    }
  },
  {
    color: "#060010",
    title: "System Integration",
    description: "Connecting disparate systems seamlessly",
    label: "Connectivity",
    media: {
      video: "https://storage.googleapis.com/tll-videos-for-web/WebsiteSkills_SystemsIntegration.mp4",
      poster: "/img/WebsiteSkills_StaticImages/SystemsIntegration.jpg",
      alt: "System integration architecture"
    },
    links: {
      github: ["https://github.com/jaywalked78/Codebase-Consolidator", "https://github.com/jaywalked78/FrameWhisperer", "https://github.com/jaywalked78/LightragFork_AdvancedNeo4JImplementation"],
      live: ["https://integration-demo.thelogicloom.com"]
    },
    chatPrompt: "Show me projects Jason has worked on that involve system integration",
    stats: {
      projects: 20,
      languages: ["Node.js", "Python", "bash scripting"],
      highlight: "Integration of various systems into front-end and back-end projects with n8n"
    }
  },
  {
    color: "#060010",
    title: "Back-End/API Dev",
    description: "Scalable server architecture & API design",
    label: "Server",
    media: {
      video: "https://storage.googleapis.com/tll-videos-for-web/WebsiteSkills_BackendAPIDev.mp4",
      poster: "/img/WebsiteSkills_StaticImages/BackendAPIDev.jpg",
      alt: "Back-end API development"
    },
    links: {
      github: ["https://github.com/jaywalked78/VideoSceneDetector", "https://github.com/jaywalked78/vidTranscriber", "https://github.com/jaywalked78/WebScribe", "https://github.com/jaywalked78/Lightweight-File-Hosting-Server"],
      live: ["https://api-demo.thelogicloom.com"]
    },
    chatPrompt: "What back-end API projects has Jason developed?",
    stats: {
      projects: 28,
      languages: ["Node.js", "Python", "FastAPI", "PostgreSQL"],
      highlight: "APIs built with FastAPI with direct integration into front-end and back-end projects with n8n"
    }
  },
];

const VideoPreview = ({ video, poster, alt, isHovered, isClicked, disableAnimations, isMobile }) => {
  const videoRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isPreloaded, setIsPreloaded] = useState(false);

  // Preload video when component mounts
  useEffect(() => {
    if (!disableAnimations && video) {
      const preloadVideo = () => {
        const videoElement = document.createElement('video');
        videoElement.src = video;
        videoElement.preload = 'metadata';
        videoElement.addEventListener('loadedmetadata', () => {
          setIsPreloaded(true);
        });
        videoElement.load();
      };
      
      // Delay preloading slightly to prioritize initial page load
      const timer = setTimeout(preloadVideo, 1000);
      return () => clearTimeout(timer);
    }
  }, [video, disableAnimations]);

  useEffect(() => {
    if (videoRef.current && !disableAnimations) {
      const shouldPlay = isMobile ? isClicked : isHovered;
      
      if (shouldPlay) {
        // Set preload to auto when about to play
        videoRef.current.preload = 'auto';
        
        // Load the video if not already loaded
        if (videoRef.current.readyState === 0) {
          videoRef.current.load();
        }
        
        const playVideo = () => {
          videoRef.current.play().catch(e => {
            console.log("Video autoplay failed:", e);
            setHasError(true);
          });
        };

        if (videoRef.current.readyState >= 3) {
          // Video is already loaded enough to play
          playVideo();
        } else {
          // Wait for video to be ready
          const handleCanPlay = () => {
            setIsLoaded(true);
            playVideo();
          };
          videoRef.current.addEventListener('canplay', handleCanPlay, { once: true });
        }
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered, isClicked, disableAnimations, isMobile]);

  const handleVideoLoad = () => {
    setIsLoaded(true);
  };

  const handleVideoError = () => {
    setHasError(true);
  };

  return (
    <div className="absolute inset-0 overflow-hidden rounded-[20px]">
      {!disableAnimations && !hasError && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          poster={poster}
          title={alt}
          className={`absolute inset-0 size-full object-cover transition-opacity duration-300 ${
            (isMobile ? isClicked : isHovered) && isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          preload={isPreloaded ? "metadata" : "none"}
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        >
          <source src={video} type="video/mp4" />
        </video>
      )}
      <img 
        src={poster} 
        alt="Video poster image"
        className={`absolute inset-0 size-full object-cover transition-opacity duration-300 ${
          (isMobile ? isClicked : isHovered) && isLoaded && !hasError && !disableAnimations ? 'opacity-0' : 'opacity-100'
        }`}
      />
      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
    </div>
  );
};

const AIChatButton = ({ category, prompt, className = "" }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    
    // Create a custom event with the prompt
    const event = new CustomEvent('openChatbotWithPrompt', {
      detail: { prompt: prompt, category: category }
    });
    window.dispatchEvent(event);
  };

  return (
    <button 
      className={`flex size-8 items-center justify-center rounded-full bg-[#090909]/85 text-white transition-all duration-200 hover:scale-110 hover:bg-[#090909]/95 ${className}`}
      onClick={handleClick}
      aria-label={`Ask AI about ${category}`}
    >
      <MessageCircle size={14} />
    </button>
  );
};

const ProjectDetailsPanel = ({ data, onClose, isOpen }) => {
  const contentRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Lock body scroll when modal opens
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      // Cleanup on close
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-[90vw] max-w-none overflow-hidden rounded-[20px] border border-gray-800 bg-gradient-to-b from-black to-gray-900 text-white md:max-w-5xl"
        style={{ 
          height: isMobile ? '95vh' : 'min(85vh, 500px)', 
          minHeight: isMobile ? '95vh' : '400px', 
          maxHeight: isMobile ? '95vh' : '90vh'
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-30 rounded-full bg-gray-800/50 p-2 text-gray-400 hover:text-white"
        >
          ×
        </button>

        {/* Responsive Layout: Desktop Side-by-Side, Mobile Vertical */}
        <div className="flex h-full flex-col md:flex-row">
          {/* Content Section - Top on Mobile (2/3 height), Right on Desktop */}
          <div className="flex w-full flex-col overflow-hidden rounded-t-[20px] md:order-2 md:h-auto md:w-1/2 md:rounded-l-none md:rounded-r-[20px]" 
               style={{ height: isMobile ? 'calc(66.67% - 1px)' : 'auto' }}>
            {/* Header */}
            <div className="shrink-0 border-b border-gray-800 p-4 md:p-6">
              <h2 className="text-base font-bold text-white md:text-lg">{data.title}</h2>
              <p className="text-sm text-gray-400 md:text-base">{data.description}</p>
            </div>

            {/* Scrollable Content */}
            <div 
              ref={contentRef}
              className="flex-1 overflow-y-auto p-4 md:p-6"
              style={{ 
                maxHeight: isMobile ? 'none' : 'calc(100vh - 200px)',
                height: isMobile ? 'calc(100% - 80px)' : 'auto'
              }}
            >
              {/* Stats */}
              <div className="mb-6 grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-gray-800/50 p-4">
                  <div className="text-2xl font-bold text-purple-400">{data.stats.projects}</div>
                  <div className="text-sm text-gray-400">Projects</div>
                </div>
                <div className="rounded-lg bg-gray-800/50 p-4">
                  <div className="text-sm text-purple-400">{data.stats.highlight}</div>
                  <div className="text-sm text-gray-400">Highlight</div>
                </div>
              </div>

              {/* Technologies */}
              <div className="mb-6">
                <h3 className="mb-3 text-lg font-semibold text-white">Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  {(data.stats.languages || data.stats.tools || []).map((tech, index) => (
                    <span
                      key={index}
                      className="rounded-full bg-purple-600/20 px-3 py-1 text-sm text-purple-300"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* GitHub Links Only - Live Demos Removed */}
              {data.links.github && (
                <div>
                  <h3 className="mb-2 text-lg font-semibold text-white">GitHub Repositories</h3>
                  <div className="space-y-2">
                    {data.links.github.map((repo, index) => (
                      <a
                        key={index}
                        href={repo.startsWith('http') ? repo : `https://github.com/${repo}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
                      >
                        <Github size={16} />
                        {repo.includes('/') ? repo.split('/').pop() : repo}
                        <ExternalLink size={14} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Separator - Horizontal on Mobile, Vertical on Desktop */}
          <div className="order-1 h-px w-full shrink-0 bg-gray-700 md:order-none md:h-auto md:w-px"></div>

          {/* Video Section - Bottom on Mobile (1/3 height), Left on Desktop */}
          <div className="relative order-3 w-full overflow-hidden rounded-b-[20px] bg-gray-800/30 md:order-1 md:h-auto md:w-1/2 md:rounded-l-[20px] md:rounded-r-none"
               style={{ height: isMobile ? 'calc(33.33% - 1px)' : 'auto' }}>
            {/* Ask AI Button in top right of video section */}
            <div className="absolute right-4 top-4 z-20">
              <AIChatButton 
                category={data.title}
                prompt={data.chatPrompt}
              />
            </div>
            <video
              className="size-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            >
              <source src={data.media.video} type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </div>
  );
};

const createParticleElement = (
  x,
  y,
  color = DEFAULT_GLOW_COLOR
) => {
  const el = document.createElement("div");
  el.className = "particle";
  el.style.cssText = `
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(${color}, 1);
    box-shadow: 0 0 6px rgba(${color}, 0.6);
    pointer-events: none;
    z-index: 100;
    left: ${x}px;
    top: ${y}px;
  `;
  return el;
};

const calculateSpotlightValues = (radius) => ({
  proximity: radius * 0.5,
  fadeDistance: radius * 0.75,
});

const updateCardGlowProperties = (
  card,
  mouseX,
  mouseY,
  glow,
  radius
) => {
  const rect = card.getBoundingClientRect();
  const relativeX = ((mouseX - rect.left) / rect.width) * 100;
  const relativeY = ((mouseY - rect.top) / rect.height) * 100;

  card.style.setProperty("--glow-x", `${relativeX}%`);
  card.style.setProperty("--glow-y", `${relativeY}%`);
  card.style.setProperty("--glow-intensity", glow.toString());
  card.style.setProperty("--glow-radius", `${radius}px`);
};

const ParticleCard = ({
  children,
  className = "",
  disableAnimations = false,
  style,
  particleCount = DEFAULT_PARTICLE_COUNT,
  glowColor = DEFAULT_GLOW_COLOR,
  enableTilt = true,
  clickEffect = false,
  enableMagnetism = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
}) => {
  const cardRef = useRef(null);
  const particlesRef = useRef([]);
  const timeoutsRef = useRef([]);
  const isHoveredRef = useRef(false);
  const memoizedParticles = useRef([]);
  const particlesInitialized = useRef(false);
  const magnetismAnimationRef = useRef(null);
  const tiltAnimationRef = useRef(null);

  const initializeParticles = useCallback(() => {
    if (particlesInitialized.current || !cardRef.current) return;

    const { width, height } = cardRef.current.getBoundingClientRect();
    memoizedParticles.current = Array.from({ length: particleCount }, () =>
      createParticleElement(
        Math.random() * width,
        Math.random() * height,
        glowColor
      )
    );
    particlesInitialized.current = true;
  }, [particleCount, glowColor]);

  const clearAllParticles = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    magnetismAnimationRef.current?.kill();

    particlesRef.current.forEach((particle) => {
      gsap.to(particle, {
        scale: 0,
        opacity: 0,
        duration: 0.3,
        ease: "back.in(1.7)",
        onComplete: () => {
          particle.parentNode?.removeChild(particle);
        },
      });
    });
    particlesRef.current = [];
  }, []);

  const animateParticles = useCallback(() => {
    if (!cardRef.current || !isHoveredRef.current) return;

    if (!particlesInitialized.current) {
      initializeParticles();
    }

    memoizedParticles.current.forEach((particle, index) => {
      const timeoutId = setTimeout(() => {
        if (!isHoveredRef.current || !cardRef.current) return;

        const clone = particle.cloneNode(true);
        cardRef.current.appendChild(clone);
        particlesRef.current.push(clone);

        gsap.fromTo(
          clone,
          { scale: 0, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.3, ease: "back.out(1.7)" }
        );

        gsap.to(clone, {
          x: (Math.random() - 0.5) * 100,
          y: (Math.random() - 0.5) * 100,
          rotation: Math.random() * 360,
          duration: 2 + Math.random() * 2,
          ease: "none",
          repeat: -1,
          yoyo: true,
        });

        gsap.to(clone, {
          opacity: 0.3,
          duration: 1.5,
          ease: "power2.inOut",
          repeat: -1,
          yoyo: true,
        });
      }, index * 100);

      timeoutsRef.current.push(timeoutId);
    });
  }, [initializeParticles]);

  useEffect(() => {
    if (disableAnimations || !cardRef.current) return;

    const element = cardRef.current;

    const handleMouseEnter = () => {
      isHoveredRef.current = true;
      animateParticles();

      if (enableTilt) {
        // Kill any existing tilt animation
        if (tiltAnimationRef.current) {
          tiltAnimationRef.current.kill();
        }
        
        tiltAnimationRef.current = gsap.to(element, {
          rotateX: 5,
          rotateY: 5,
          duration: 0.1,
          ease: "power2.out",
          transformPerspective: 1000,
          overwrite: true,
        });
      }
    };

    const handleMouseLeave = () => {
      isHoveredRef.current = false;
      clearAllParticles();

      if (enableTilt) {
        // Kill any existing tilt animation
        if (tiltAnimationRef.current) {
          tiltAnimationRef.current.kill();
        }
        
        tiltAnimationRef.current = gsap.to(element, {
          rotateX: 0,
          rotateY: 0,
          duration: 0.1,
          ease: "power2.out",
          overwrite: true,
        });
      }

      if (enableMagnetism) {
        gsap.to(element, {
          x: 0,
          y: 0,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    };

    const handleMouseMove = (e) => {
      if (!enableTilt && !enableMagnetism) return;

      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      if (enableTilt) {
        const rotateX = ((y - centerY) / centerY) * -10;
        const rotateY = ((x - centerX) / centerX) * 10;

        // Kill any existing tilt animation
        if (tiltAnimationRef.current) {
          tiltAnimationRef.current.kill();
        }

        tiltAnimationRef.current = gsap.to(element, {
          rotateX,
          rotateY,
          duration: 0.1,
          ease: "power2.out",
          transformPerspective: 1000,
          overwrite: true,
        });
      }

      if (enableMagnetism) {
        const magnetX = (x - centerX) * 0.05;
        const magnetY = (y - centerY) * 0.05;

        magnetismAnimationRef.current = gsap.to(element, {
          x: magnetX,
          y: magnetY,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    };

    const handleClick = (e) => {
      if (!clickEffect) return;

      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const maxDistance = Math.max(
        Math.hypot(x, y),
        Math.hypot(x - rect.width, y),
        Math.hypot(x, y - rect.height),
        Math.hypot(x - rect.width, y - rect.height)
      );

      const ripple = document.createElement("div");
      ripple.style.cssText = `
        position: absolute;
        width: ${maxDistance * 2}px;
        height: ${maxDistance * 2}px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(${glowColor}, 0.4) 0%, rgba(${glowColor}, 0.2) 30%, transparent 70%);
        left: ${x - maxDistance}px;
        top: ${y - maxDistance}px;
        pointer-events: none;
        z-index: 1000;
      `;

      element.appendChild(ripple);

      gsap.fromTo(
        ripple,
        {
          scale: 0,
          opacity: 1,
        },
        {
          scale: 1,
          opacity: 0,
          duration: 0.8,
          ease: "power2.out",
          onComplete: () => ripple.remove(),
        }
      );
    };

    element.addEventListener("mouseenter", handleMouseEnter);
    element.addEventListener("mouseleave", handleMouseLeave);
    element.addEventListener("mousemove", handleMouseMove);
    element.addEventListener("click", handleClick);

    return () => {
      isHoveredRef.current = false;
      element.removeEventListener("mouseenter", handleMouseEnter);
      element.removeEventListener("mouseleave", handleMouseLeave);
      element.removeEventListener("mousemove", handleMouseMove);
      element.removeEventListener("click", handleClick);
      clearAllParticles();
      
      // Clean up animations
      if (tiltAnimationRef.current) {
        tiltAnimationRef.current.kill();
      }
      if (magnetismAnimationRef.current) {
        magnetismAnimationRef.current.kill();
      }
    };
  }, [
    animateParticles,
    clearAllParticles,
    disableAnimations,
    enableTilt,
    enableMagnetism,
    clickEffect,
    glowColor,
  ]);

  return (
    <div
      ref={cardRef}
      className={`${className} relative overflow-hidden`}
      style={{ ...style, position: "relative", overflow: "hidden" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

const GlobalSpotlight = ({
  gridRef,
  disableAnimations = false,
  enabled = true,
  spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
  glowColor = DEFAULT_GLOW_COLOR,
}) => {
  const spotlightRef = useRef(null);
  const isInsideSection = useRef(false);

  useEffect(() => {
    if (disableAnimations || !gridRef?.current || !enabled) return;

    const spotlight = document.createElement("div");
    spotlight.className = "global-spotlight";
    spotlight.style.cssText = `
      position: fixed;
      width: 800px;
      height: 800px;
      border-radius: 50%;
      pointer-events: none;
      background: radial-gradient(circle,
        rgba(${glowColor}, 0.15) 0%,
        rgba(${glowColor}, 0.08) 15%,
        rgba(${glowColor}, 0.04) 25%,
        rgba(${glowColor}, 0.02) 40%,
        rgba(${glowColor}, 0.01) 65%,
        transparent 70%
      );
      z-index: 200;
      opacity: 0;
      transform: translate(-50%, -50%);
      mix-blend-mode: screen;
    `;
    document.body.appendChild(spotlight);
    spotlightRef.current = spotlight;

    const handleMouseMove = (e) => {
      if (!spotlightRef.current || !gridRef.current) return;

      const section = gridRef.current.closest(".bento-section");
      const rect = section?.getBoundingClientRect();
      const mouseInside =
        rect &&
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      isInsideSection.current = mouseInside || false;
      const cards = gridRef.current.querySelectorAll(".card");

      if (!mouseInside) {
        gsap.to(spotlightRef.current, {
          opacity: 0,
          duration: 0.3,
          ease: "power2.out",
        });
        cards.forEach((card) => {
          card.style.setProperty("--glow-intensity", "0");
        });
        return;
      }

      const { proximity, fadeDistance } =
        calculateSpotlightValues(spotlightRadius);
      let minDistance = Infinity;

      cards.forEach((card) => {
        const cardElement = card;
        const cardRect = cardElement.getBoundingClientRect();
        const centerX = cardRect.left + cardRect.width / 2;
        const centerY = cardRect.top + cardRect.height / 2;
        const distance =
          Math.hypot(e.clientX - centerX, e.clientY - centerY) -
          Math.max(cardRect.width, cardRect.height) / 2;
        const effectiveDistance = Math.max(0, distance);

        minDistance = Math.min(minDistance, effectiveDistance);

        let glowIntensity = 0;
        if (effectiveDistance <= proximity) {
          glowIntensity = 1;
        } else if (effectiveDistance <= fadeDistance) {
          glowIntensity =
            (fadeDistance - effectiveDistance) / (fadeDistance - proximity);
        }

        updateCardGlowProperties(
          cardElement,
          e.clientX,
          e.clientY,
          glowIntensity,
          spotlightRadius
        );
      });

      gsap.to(spotlightRef.current, {
        left: e.clientX,
        top: e.clientY,
        duration: 0.1,
        ease: "power2.out",
      });

      const targetOpacity =
        minDistance <= proximity
          ? 0.8
          : minDistance <= fadeDistance
            ? ((fadeDistance - minDistance) / (fadeDistance - proximity)) * 0.8
            : 0;

      gsap.to(spotlightRef.current, {
        opacity: targetOpacity,
        duration: targetOpacity > 0 ? 0.2 : 0.5,
        ease: "power2.out",
      });
    };

    const handleMouseLeave = () => {
      isInsideSection.current = false;
      gridRef.current?.querySelectorAll(".card").forEach((card) => {
        card.style.setProperty("--glow-intensity", "0");
      });
      if (spotlightRef.current) {
        gsap.to(spotlightRef.current, {
          opacity: 0,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
      spotlightRef.current?.parentNode?.removeChild(spotlightRef.current);
    };
  }, [gridRef, disableAnimations, enabled, spotlightRadius, glowColor]);

  return null;
};

const BentoCardGrid = ({ children, gridRef }) => (
  <div
    className="bento-section mx-auto w-full p-3 md:w-[95%]"
    ref={gridRef}
  >
    {children}
  </div>
);

const useMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () =>
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

const EnhancedPortfolioCard = ({ 
  cardData, 
  index, 
  baseClassName, 
  cardStyle, 
  shouldDisableAnimations, 
  enableStars, 
  enableTilt, 
  enableMagnetism, 
  clickEffect, 
  particleCount, 
  glowColor, 
  textAutoHide,
  isMobile,
  vignettingStyle 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handleCardClick = () => {
    // Open details on both mobile and desktop
    setShowDetails(true);
  };

  const handleVideoAreaClick = () => {
    if (isMobile) {
      setIsClicked(!isClicked);
    }
  };

  if (enableStars) {
    return (
      <>
        <ParticleCard
          key={index}
          className={`${baseClassName} group cursor-pointer`}
          style={cardStyle}
          disableAnimations={shouldDisableAnimations}
          particleCount={particleCount}
          glowColor={glowColor}
          enableTilt={enableTilt}
          clickEffect={clickEffect}
          enableMagnetism={enableMagnetism}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleCardClick}
        >
          {/* Video Background */}
          <div onClick={handleVideoAreaClick}>
            <VideoPreview 
              {...cardData.media} 
              isHovered={isHovered}
              isClicked={isClicked}
              disableAnimations={shouldDisableAnimations}
              isMobile={isMobile}
            />
          </div>
          
          {/* Vignetting Overlay */}
          <div style={vignettingStyle}></div>
          
          {/* Content */}
          <div className="relative z-10 flex h-full flex-col justify-between p-5">
            {/* Ask AI button in top right */}
            <div className="flex justify-end">
              <AIChatButton 
                category={cardData.title}
                prompt={cardData.chatPrompt}
              />
            </div>
            
            {/* Title in bottom left */}
            <h3 className="text-base font-bold text-white">
              {cardData.title}
            </h3>
          </div>
        </ParticleCard>
        
        {/* Project Details Modal */}
        <ProjectDetailsPanel 
          data={cardData}
          onClose={() => setShowDetails(false)}
          isOpen={showDetails}
        />
      </>
    );
  }

  // Non-particle version with same enhancements
  return (
    <>
      <div
        key={index}
        className={`${baseClassName} group cursor-pointer`}
        style={cardStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCardClick}
      >
        {/* Video Background */}
        <div onClick={handleVideoAreaClick}>
          <VideoPreview 
            {...cardData.media} 
            isHovered={isHovered}
            isClicked={isClicked}
            disableAnimations={shouldDisableAnimations}
            isMobile={isMobile}
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex h-full flex-col justify-between p-5">
          <div className="flex justify-between gap-3 text-white">
            <span className="text-base">{cardData.label}</span>
            <AIChatButton 
              category={cardData.title}
              prompt={cardData.chatPrompt}
            />
          </div>
          
          <div className="flex flex-col text-white">
            <h3 className={`mb-1 text-sm font-bold ${textAutoHide ? "text-clamp-1" : ""}`}>
              {cardData.title}
            </h3>
            <p className={`text-xs leading-5 opacity-90 ${textAutoHide ? "text-clamp-2" : ""}`}>
              {cardData.description}
            </p>
            
            {/* Stats that appear on hover */}
            {isHovered && (
              <div className="mt-2 space-y-1 text-xs opacity-80">
                <div>{cardData.stats.projects} projects</div>
                <div className="text-purple-300">{cardData.stats.highlight}</div>
              </div>
            )}
          </div>
          
          {/* View Projects Button */}
          {isHovered && (
            <div className="absolute bottom-4 right-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <div className="flex items-center gap-1 text-xs text-purple-300">
                View Projects <ArrowRight size={12} />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Project Details Modal */}
      <ProjectDetailsPanel 
        data={cardData}
        onClose={() => setShowDetails(false)}
        isOpen={showDetails}
      />
    </>
  );
};

const MagicBento = ({
  textAutoHide = true,
  enableStars = true,
  enableSpotlight = true,
  enableBorderGlow = true,
  disableAnimations = false,
  spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
  particleCount = DEFAULT_PARTICLE_COUNT,
  enableTilt = false,
  glowColor = DEFAULT_GLOW_COLOR,
  clickEffect = true,
  enableMagnetism = true,
}) => {
  const gridRef = useRef(null);
  const isMobile = useMobileDetection();
  const shouldDisableAnimations = disableAnimations || isMobile;

  return (
    <div
      style={{
        "--glow-color-rgb": glowColor,
      }}
    >
      {enableSpotlight && (
        <GlobalSpotlight
          gridRef={gridRef}
          disableAnimations={shouldDisableAnimations}
          enabled={enableSpotlight}
          spotlightRadius={spotlightRadius}
          glowColor={glowColor}
        />
      )}

      <BentoCardGrid gridRef={gridRef}>
        <div className="card-responsive grid w-full gap-2" style={{ fontSize: "clamp(1rem, 0.9rem + 0.5vw, 1.5rem)" }}>
          {cardData.map((card, index) => {
            const baseClassName = `card relative flex aspect-[4/3] min-h-[200px] w-full max-w-full flex-col justify-between overflow-hidden rounded-[20px] border border-solid font-light transition-all duration-300 ease-in-out hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)] ${enableBorderGlow ? "card--border-glow" : ""
              }`;
            
            // Add vignetting overlay style
            const vignettingStyle = {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(to top, rgba(0,0,0,0.25) 0%, transparent 40%)`,
              pointerEvents: 'none',
              zIndex: 1
            };

            const cardStyle = {
              backgroundColor: card.color || "var(--background-dark)",
              borderColor: "var(--border-color)",
              color: "var(--white)",
              "--glow-x": "50%",
              "--glow-y": "50%",
              "--glow-intensity": "0",
              "--glow-radius": "200px",
            };

            return (
              <EnhancedPortfolioCard
                key={index}
                cardData={card}
                index={index}
                baseClassName={baseClassName}
                cardStyle={cardStyle}
                shouldDisableAnimations={shouldDisableAnimations}
                enableStars={enableStars}
                enableTilt={enableTilt}
                enableMagnetism={enableMagnetism}
                clickEffect={clickEffect}
                particleCount={particleCount}
                glowColor={glowColor}
                textAutoHide={textAutoHide}
                isMobile={isMobile}
                vignettingStyle={vignettingStyle}
              />
            );
          })}
        </div>
      </BentoCardGrid>
    </div>
  );
};

export default MagicBento;
